import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Star, ShieldCheck, Clock, Brain, Heart, Ban, TrendingUp, BookOpen, ChevronDown, ChevronUp, Lock, Zap, Gift, Download } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} className={className} data-id="element-15">
    {children}
  </motion.div>;
export function LandingPage() {
  // Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 58,
    seconds: 3
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            ...prev,
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedPack, setSelectedPack] = useState<'digital' | 'premium'>('premium');
  const CHECKOUT_URLS = {
    digital: 'https://indigo33.shop/cart/64134545604977:1',
    premium: 'https://indigo33.shop/cart/64134548750705:1'
  };
  const goToCheckout = () => {
    window.location.href = CHECKOUT_URLS[selectedPack];
  };
  const faqs = [{
    q: '¿Cómo recibo el libro?',
    a: 'Inmediatamente después de confirmar tu compra recibirás un correo con el acceso para descargar todo el material en formato PDF.'
  }, {
    q: '¿También puedo leerlo desde el celular?',
    a: 'Sí. Podés leerlo desde celular, tablet o computadora cuando quieras.'
  }, {
    q: '¿Necesito conocimientos previos?',
    a: 'No. Está escrito para cualquier persona que quiera mejorar su bienestar emocional.'
  }, {
    q: '¿Cuándo tendré acceso?',
    a: 'El acceso es inmediato después del pago.'
  }, {
    q: '¿Qué métodos de pago aceptan?',
    a: 'Tarjetas de crédito, débito, transferencias y otros medios disponibles en la plataforma de pago.'
  }];
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#FAFAFA] font-['Inter'] text-slate-800 overflow-x-hidden" data-id="element-16">
      {/* Pop-up de compras recientes (cada 12 segundos) */}
      <PurchaseNotification data-id="element-17" />

      {/* Top Banner */}
      <div className="bg-[#E85D75] text-white text-center py-2 px-4 text-xs sm:text-sm font-medium pt-[2px] pb-[2px]" data-id="element-18">
        🔥 Más de 3.000 lectores ya empezaron a entenderse mejor
      </div>

      {/* Hero Section */}
      <section className="pt-8 pb-12 md:pt-16 md:pb-20 px-4 md:px-8 max-w-7xl mx-auto grid md:grid-cols-2 gap-6 md:gap-12 items-center" data-id="element-19">
        <div className="space-y-4 md:space-y-8" data-id="element-20">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-21">
            <h2 className="text-[#E85D75] font-bold tracking-wider text-xs sm:text-sm uppercase mb-2 md:mb-3" data-id="element-22">
              Terapia Para Llevar™
            </h2>
            <h1 className="md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-slate-900 mb-4 md:mb-6 text-[25px]" data-id="element-23">
              100 herramientas prácticas para cuidar tu salud mental, entender
              tus emociones y transformar tu día a día.
            </h1>

            {/* Imagen del producto — visible justo debajo del título en mobile */}
            <div className="relative mb-4 md:hidden" data-id="element-24">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#E85D75]/20 to-transparent rounded-2xl transform rotate-2 scale-105 -z-10" data-id="element-25"></div>
              <img src="https://cdn.magicpatterns.com/uploads/quL9JZhKKihRzRGdqyfXgq/722b11a7-10ee-42b9-a7d4-b389430c3f94.png" alt="Terapia Para Llevar Libro y Móvil" className="w-full h-auto rounded-xl shadow-xl" data-id="element-26" />
            </div>

            <p className="md:text-lg mb-4 md:mb-8 text-[13px] text-[#080808]" data-id="element-27">
              Dejá de sobrevivir en piloto automático. Aprendé a conocerte,
              manejar tus pensamientos y construir una vida con más calma,
              autoestima y bienestar.
            </p>

            <button onClick={scrollToPricing} className="w-full sm:w-auto bg-[#E85D75] hover:bg-[#D44A62] text-white text-lg font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center pt-[14px] pb-[14px] gap-[7px]" data-id="element-28">
              👉 QUIERO EMPEZAR HOY
            </button>

            {/* Beneficios en tarjetas delicadas — debajo del CTA */}
            <div className="grid grid-cols-2 gap-2 md:gap-3 mt-4 md:mt-6" data-id="element-29">
              {[{
              icon: Brain,
              label: 'Controlá los pensamientos intrusivos'
            }, {
              icon: Heart,
              label: 'Aumentá tu autoestima y confianza'
            }, {
              icon: Zap,
              label: 'Dejá de procrastinar y actuá'
            }, {
              icon: Download,
              label: 'Acceso digital inmediato (PDF)'
            }].map((item, i) => <div key={i} className="flex items-center gap-2.5 bg-[#FBF6EE] rounded-xl px-3 py-2.5" data-id="element-30">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#E4EEF3] flex items-center justify-center" data-id="element-31">
                    <item.icon className="text-slate-700" size={16} data-id="element-32" />
                  </span>
                  <span className="text-[11px] md:text-xs font-medium text-slate-700 leading-snug" data-id="element-33">
                    {item.label}
                  </span>
                </div>)}
            </div>

            <div className="flex flex-wrap mt-4 text-sm text-slate-500 font-medium gap-[1px] items-start justify-center" data-id="element-34">
              <span className="flex items-center gap-1" data-id="element-35">
                <Lock size={16} data-id="element-36" /> Pago Seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-37">
                <Zap size={16} data-id="element-38" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-1" data-id="element-39">
                <ShieldCheck size={16} data-id="element-40" /> Garantía de 7 días
              </span>
            </div>
          </motion.div>
        </div>

        {/* Imagen del producto — solo desktop (columna derecha) */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8
      }} className="relative hidden md:block" data-id="element-41">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#E85D75]/20 to-transparent rounded-3xl transform rotate-3 scale-105 -z-10" data-id="element-42"></div>
          <img src="https://cdn.magicpatterns.com/uploads/quL9JZhKKihRzRGdqyfXgq/722b11a7-10ee-42b9-a7d4-b389430c3f94.png" alt="Terapia Para Llevar Libro y Móvil" className="w-full h-auto rounded-2xl shadow-2xl" data-id="element-43" />
        </motion.div>
      </section>

      {/* Problem Agitation */}
      <section className="bg-slate-900 text-white py-12 md:py-24 px-4" data-id="element-44">
        <div className="max-w-4xl mx-auto" data-id="element-45">
          <FadeIn data-id="element-46">
            <h2 className="text-2xl md:text-5xl font-['Outfit'] font-bold text-center mb-8 md:mb-16 leading-tight" data-id="element-47">
              ¿HASTA CUÁNDO VAS A SEGUIR IGNORANDO LO QUE SENTÍS?
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-8 md:mb-16" data-id="element-48">
            <FadeIn delay={0.2} className="space-y-3 md:space-y-6 text-base md:text-lg text-slate-300" data-id="element-49">
              <p data-id="element-50">
                <strong className="text-white" data-id="element-51">No estás roto.</strong>
              </p>
              <p data-id="element-52">
                <strong className="text-white" data-id="element-53">No sos débil.</strong>
              </p>
              <p data-id="element-54">
                <strong className="text-white" data-id="element-55">
                  No te falta fuerza de voluntad.
                </strong>
              </p>
              <p data-id="element-56">
                Simplemente nadie te enseñó a entender lo que pasa dentro de tu
                cabeza.
              </p>
              <p data-id="element-57">Vivís con ansiedad sin saber por qué.</p>
              <p data-id="element-58">Te cuesta apagar los pensamientos.</p>
              <p data-id="element-59">Sentís que algo no está bien, pero no sabés ponerle nombre.</p>
              <p data-id="element-60">
                Y mientras seguís intentando con consejos de internet... La
                frustración crece.
              </p>
            </FadeIn>

            <FadeIn delay={0.4} className="bg-slate-800 rounded-2xl p-8 border border-slate-700" data-id="element-61">
              <h3 className="text-xl font-bold mb-6 text-[#E85D75]" data-id="element-62">
                ¿Te pasa alguna de estas situaciones?
              </h3>
              <ul className="space-y-6" data-id="element-63">
                {[{
                emoji: '😔',
                title: 'Pensás demasiado todo',
                desc: 'Tu cabeza nunca descansa.'
              }, {
                emoji: '💭',
                title: 'Los pensamientos negativos te consumen',
                desc: 'Imaginás escenarios que nunca suceden.'
              }, {
                emoji: '🙍',
                title: 'La opinión de los demás define cómo te sentís',
                desc: 'Vivís buscando aprobación.'
              }, {
                emoji: '🚫',
                title: 'No sabés poner límites',
                desc: 'Terminás haciendo cosas que no querés.'
              }, {
                emoji: '😞',
                title: 'Procrastinás constantemente',
                desc: 'Sabés lo que tenés que hacer... pero nunca empezás.'
              }, {
                emoji: '❤️',
                title: 'Sentís que tu autoestima desapareció',
                desc: 'Dudás de vos incluso cuando hacés las cosas bien.'
              }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-64">
                    <span className="text-2xl" data-id="element-65">{item.emoji}</span>
                    <div data-id="element-66">
                      <h4 className="font-bold text-white" data-id="element-67">{item.title}</h4>
                      <p className="text-sm text-slate-400" data-id="element-68">{item.desc}</p>
                    </div>
                  </li>)}
              </ul>
            </FadeIn>
          </div>

          <FadeIn delay={0.6} className="text-center" data-id="element-69">
            <blockquote className="text-xl md:text-3xl font-['Outfit'] font-medium italic text-slate-300 mb-6 md:mb-10" data-id="element-70">
              "La salud mental no mejora por casualidad. Mejora cuando empezás a
              entenderte y actuás todos los días."
            </blockquote>
          </FadeIn>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-12 md:py-24 px-4 bg-white" data-id="element-71">
        <div className="max-w-5xl mx-auto text-center" data-id="element-72">
          <FadeIn data-id="element-73">
            <h2 className="text-2xl md:text-4xl font-['Outfit'] font-bold text-slate-900 mb-2 md:mb-4" data-id="element-74">
              ESTE LIBRO NO ES SOLO PARA LEER.
            </h2>
            <h3 className="text-xl md:text-3xl font-['Outfit'] font-bold text-[#E85D75] mb-6 md:mb-12" data-id="element-75">
              ES PARA EMPEZAR A CAMBIAR.
            </h3>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-8 md:mb-16" data-id="element-76">
              <strong data-id="element-77">Terapia Para Llevar™</strong> reúne 100 herramientas
              prácticas, simples y fáciles de aplicar para ayudarte a mejorar tu
              bienestar emocional. No encontrarás teoría complicada. Encontrarás
              ejercicios. Reflexiones. Herramientas. Y estrategias que podés
              aplicar desde el primer día.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-78">
            <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-10" data-id="element-79">
              LO QUE VAS A CONSEGUIR CON ESTE LIBRO
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 text-left" data-id="element-80">
              {[{
              icon: Brain,
              title: 'Entender tus emociones',
              desc: 'Aprendé a identificar lo que realmente sentís.'
            }, {
              icon: TrendingUp,
              title: 'Fortalecer tu autoestima',
              desc: 'Construí una relación más sana con vos mismo.'
            }, {
              icon: Ban,
              title: 'Controlar pensamientos',
              desc: 'Dejá de darle poder a tu ansiedad.'
            }, {
              icon: Clock,
              title: 'Vencer la procrastinación',
              desc: 'Transformá la acción en un hábito.'
            }, {
              icon: ShieldCheck,
              title: 'Poner límites saludables',
              desc: 'Aprendé a decir NO sin culpa.'
            }, {
              icon: Heart,
              title: 'Gestionar el miedo',
              desc: 'Viví más presente y con menos ansiedad.'
            }, {
              icon: Star,
              title: 'Superar decepciones',
              desc: 'Saná emocionalmente y seguí adelante.'
            }, {
              icon: BookOpen,
              title: 'Conocerte profundamente',
              desc: 'Porque la persona más importante sos vos.'
            }].map((feature, i) => <div key={i} className="bg-slate-50 p-4 md:p-6 rounded-2xl border border-slate-100 hover:border-[#E85D75]/30 transition-colors" data-id="element-81">
                  <feature.icon className="text-[#E85D75] mb-2 md:mb-4" size={28} data-id="element-82" />
                  <h4 className="font-bold text-slate-900 mb-1 md:mb-2 text-sm md:text-base" data-id="element-83">
                    {feature.title}
                  </h4>
                  <p className="text-xs md:text-sm text-slate-600" data-id="element-84">
                    {feature.desc}
                  </p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-12 md:py-24 px-4 bg-[#FFF5F7]" data-id="element-85">
        <div className="max-w-5xl mx-auto" data-id="element-86">
          <FadeIn className="text-center mb-8 md:mb-16" data-id="element-87">
            <div className="inline-flex items-center justify-center gap-2 bg-[#E85D75] text-white px-4 py-1.5 rounded-full text-xs md:text-sm font-bold mb-4 md:mb-6 uppercase tracking-wider" data-id="element-88">
              <Gift size={16} data-id="element-89" /> Oferta Especial
            </div>
            <h2 className="text-2xl md:text-5xl font-['Outfit'] font-bold text-slate-900 mb-3 md:mb-4" data-id="element-90">
              4 BONOS EXCLUSIVOS INCLUIDOS GRATIS
            </h2>
            <p className="text-lg md:text-xl text-slate-600" data-id="element-91">
              Valor Total: <span className="line-through" data-id="element-92">$52.000 ARS</span>{' '}
              <span className="text-[#E85D75] font-bold" data-id="element-93">¡HOY GRATIS!</span>
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-94">
            {[{
            num: 1,
            title: 'Diario de Transformación Personal',
            desc: 'Workbook imprimible de 21 días. Reflexiones, ejercicios y espacios para escribir y reconectar con vos.',
            value: '$12.000'
          }, {
            num: 2,
            title: 'El Poder de Dejar Ir',
            desc: 'Rompe con Relaciones Tóxicas. Aprendé a identificar vínculos dañinos y recuperar tu bienestar emocional.',
            value: '$15.000'
          }, {
            num: 3,
            title: 'Primero Yo, Después el Resto',
            desc: '120 sesiones de autoconocimiento y empoderamiento personal.',
            value: '$10.000'
          }, {
            num: 4,
            title: 'Cómo Cambiar Tu Vida en 50 Hojas',
            desc: 'Guía práctica para fortalecer autoestima, amor propio y aceptación.',
            value: '$15.000'
          }].map((bonus, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-95">
                <div className="bg-white p-5 md:p-8 rounded-2xl shadow-sm border border-pink-100 flex gap-4 md:gap-6 items-start" data-id="element-96">
                  <div className="bg-pink-100 text-[#E85D75] w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold text-lg md:text-xl shrink-0" data-id="element-97">
                    {bonus.num}
                  </div>
                  <div data-id="element-98">
                    <h4 className="text-xs font-bold text-[#E85D75] uppercase tracking-wider mb-1" data-id="element-99">
                      BONUS {bonus.num}
                    </h4>
                    <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-1 md:mb-2" data-id="element-100">
                      {bonus.title}
                    </h3>
                    <p className="text-sm md:text-base text-slate-600 mb-2 md:mb-3" data-id="element-101">
                      {bonus.desc}
                    </p>
                    <p className="text-sm font-medium text-slate-400" data-id="element-102">
                      Valor: <span className="line-through" data-id="element-103">{bonus.value}</span>
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 md:py-24 px-4 bg-slate-900 text-white" data-id="element-104">
        <div className="max-w-5xl mx-auto" data-id="element-105">
          <FadeIn className="text-center mb-8 md:mb-12" data-id="element-106">
            <h2 className="text-2xl md:text-5xl font-['Outfit'] font-bold mb-4 md:mb-6" data-id="element-107">
              🔥 OFERTA ESPECIAL DE LANZAMIENTO
            </h2>

            {/* Timer */}
            <div className="inline-block bg-slate-800 border border-slate-700 rounded-2xl p-4 md:p-6 mb-4 md:mb-6" data-id="element-108">
              <p className="text-slate-400 text-sm font-bold uppercase tracking-wider mb-4" data-id="element-109">
                ESTA OFERTA TERMINA EN
              </p>
              <div className="flex gap-4 text-center" data-id="element-110">
                <div className="bg-slate-900 rounded-lg p-3 w-20" data-id="element-111">
                  <span className="text-3xl font-bold text-[#E85D75]" data-id="element-112">
                    {String(timeLeft.hours).padStart(2, '0')}
                  </span>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-113">Horas</p>
                </div>
                <span className="text-3xl font-bold text-slate-600 self-center" data-id="element-114">
                  :
                </span>
                <div className="bg-slate-900 rounded-lg p-3 w-20" data-id="element-115">
                  <span className="text-3xl font-bold text-[#E85D75]" data-id="element-116">
                    {String(timeLeft.minutes).padStart(2, '0')}
                  </span>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-117">Minutos</p>
                </div>
                <span className="text-3xl font-bold text-slate-600 self-center" data-id="element-118">
                  :
                </span>
                <div className="bg-slate-900 rounded-lg p-3 w-20" data-id="element-119">
                  <span className="text-3xl font-bold text-[#E85D75]" data-id="element-120">
                    {String(timeLeft.seconds).padStart(2, '0')}
                  </span>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-121">Segundos</p>
                </div>
              </div>
            </div>
            <p className="text-yellow-400 font-medium flex items-center justify-center gap-2" data-id="element-122">
              ⚠️ Después vuelve a precio normal.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="max-w-2xl mx-auto" data-id="element-123">
            <h3 className="text-center text-xl md:text-2xl font-['Outfit'] font-bold mb-6" data-id="element-124">
              ELEGÍ TU PACK
            </h3>

            <div className="space-y-4" data-id="element-125">
              {/* Pack Digital */}
              <button type="button" onClick={() => setSelectedPack('digital')} aria-pressed={selectedPack === 'digital'} className={`w-full text-left rounded-2xl p-5 md:p-6 border-2 transition-all flex items-start gap-4 ${selectedPack === 'digital' ? 'border-[#E85D75] bg-slate-800' : 'border-slate-700 bg-slate-800/60 hover:border-slate-600'}`} data-id="element-126">
                <span className={`shrink-0 mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 'digital' ? 'border-[#E85D75]' : 'border-slate-500'}`} data-id="element-127">
                  {selectedPack === 'digital' && <span className="w-2.5 h-2.5 rounded-full bg-[#E85D75]" data-id="element-128" />}
                </span>

                <div className="flex-1 min-w-0" data-id="element-129">
                  <div className="flex justify-between items-start gap-3" data-id="element-130">
                    <div data-id="element-131">
                      <div className="flex items-center gap-2 mb-1" data-id="element-132">
                        <BookOpen size={18} className="text-[#E85D75]" data-id="element-133" />
                        <h4 className="text-lg md:text-xl font-bold text-white" data-id="element-134">
                          PACK DIGITAL
                        </h4>
                      </div>
                      <p className="text-sm text-slate-400" data-id="element-135">
                        Libro (PDF) + 4 Bonos Exclusivos
                      </p>
                      <p className="text-sm text-[#E85D75] font-medium mt-1" data-id="element-136">
                        Ideal para: empezar tu camino hoy
                      </p>
                    </div>
                    <div className="text-right shrink-0" data-id="element-137">
                      <span className="block text-sm text-slate-500 line-through" data-id="element-138">
                        $49.999
                      </span>
                      <span className="text-2xl md:text-3xl font-extrabold text-white whitespace-nowrap" data-id="element-139">
                        $19.999
                      </span>
                      <span className="block text-xs text-slate-400" data-id="element-140">ARS</span>
                    </div>
                  </div>
                </div>
              </button>

              {/* Pack Premium */}
              <button type="button" onClick={() => setSelectedPack('premium')} aria-pressed={selectedPack === 'premium'} className={`relative w-full text-left rounded-2xl p-5 md:p-6 border-2 transition-all flex items-start gap-4 ${selectedPack === 'premium' ? 'border-[#E85D75] bg-slate-800' : 'border-slate-700 bg-slate-800/60 hover:border-slate-600'}`} data-id="element-141">
                <span className="absolute top-0 right-5 -translate-y-1/2 bg-yellow-400 text-slate-900 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1" data-id="element-142">
                  <Star size={10} fill="currentColor" data-id="element-143" /> El más elegido
                </span>

                <span className={`shrink-0 mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 'premium' ? 'border-[#E85D75]' : 'border-slate-500'}`} data-id="element-144">
                  {selectedPack === 'premium' && <span className="w-2.5 h-2.5 rounded-full bg-[#E85D75]" data-id="element-145" />}
                </span>

                <div className="flex-1 min-w-0" data-id="element-146">
                  <div className="flex justify-between items-start gap-3" data-id="element-147">
                    <div data-id="element-148">
                      <div className="flex items-center gap-2 mb-1" data-id="element-149">
                        <span className="text-lg" data-id="element-150">🏆</span>
                        <h4 className="text-lg md:text-xl font-bold text-white" data-id="element-151">
                          PACK PREMIUM
                        </h4>
                      </div>
                      <p className="text-sm text-slate-400" data-id="element-152">
                        Libro + 4 Bonos + Audiolibro Completo
                      </p>
                      <p className="text-sm text-[#E85D75] font-medium mt-1" data-id="element-153">
                        Ideal para: la experiencia completa
                      </p>
                    </div>
                    <div className="text-right shrink-0" data-id="element-154">
                      <span className="block text-sm text-slate-500 line-through" data-id="element-155">
                        $69.999
                      </span>
                      <span className="text-2xl md:text-3xl font-extrabold text-[#E85D75] whitespace-nowrap" data-id="element-156">
                        $29.999
                      </span>
                      <span className="block text-xs text-slate-400" data-id="element-157">ARS</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* CTA */}
            <button onClick={goToCheckout} className="w-full mt-6 bg-[#E85D75] hover:bg-[#D44A62] text-white text-base md:text-lg font-bold py-4 md:py-5 rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5" data-id="element-158">
              👉 QUIERO EL{' '}
              {selectedPack === 'premium' ? 'PACK PREMIUM' : 'PACK DIGITAL'}
            </button>

            <div className="flex justify-center gap-4 mt-4 text-xs text-slate-400 font-medium" data-id="element-159">
              <span className="flex items-center gap-1" data-id="element-160">
                <Lock size={12} data-id="element-161" /> Pago Seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-162">
                <Zap size={12} data-id="element-163" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-1" data-id="element-164">
                <ShieldCheck size={12} data-id="element-165" /> Garantía de 7 días
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-24 px-4 bg-white" data-id="element-166">
        <div className="max-w-6xl mx-auto" data-id="element-167">
          <FadeIn className="text-center mb-8 md:mb-16" data-id="element-168">
            <h2 className="text-2xl md:text-4xl font-['Outfit'] font-bold text-slate-900 mb-4" data-id="element-169">
              LO QUE DICEN QUIENES YA LO LEYERON
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6" data-id="element-170">
            {[{
            text: 'Pensé que era otro libro de autoayuda, pero terminé aplicando ejercicios que realmente cambiaron mi forma de pensar.',
            author: 'María G.',
            loc: 'Córdoba'
          }, {
            text: 'Me ayudó muchísimo con la ansiedad y con esos pensamientos que no podía controlar.',
            author: 'Lucía M.',
            loc: 'Buenos Aires'
          }, {
            text: 'Cada capítulo tiene herramientas que podés aplicar en el momento. Es como tener un terapeuta en casa.',
            author: 'Valentina R.',
            loc: 'Rosario'
          }, {
            text: 'Aprendí a poner límites sin sentir culpa. Solo por eso ya valió completamente la pena.',
            author: 'Carolina P.',
            loc: 'Mendoza'
          }, {
            text: 'Lo recomiendo a cualquiera que quiera empezar a sentirse mejor sin esperar que todo cambie por arte de magia.',
            author: 'Sofía L.',
            loc: 'La Plata'
          }].map((testimonial, i) => <FadeIn key={i} delay={i * 0.1} className="bg-slate-50 p-5 md:p-8 rounded-2xl border border-slate-100" data-id="element-171">
                <div className="flex text-yellow-400 mb-3 md:mb-4" data-id="element-172">
                  {[...Array(5)].map((_, j) => <Star key={j} fill="currentColor" size={16} data-id="element-173" />)}
                </div>
                <p className="text-sm md:text-base text-slate-700 italic mb-4 md:mb-6" data-id="element-174">
                  "{testimonial.text}"
                </p>
                <div data-id="element-175">
                  <p className="font-bold text-slate-900" data-id="element-176">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-177">{testimonial.loc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-12 md:py-20 px-4 bg-[#FFF5F7]" data-id="element-178">
        <div className="max-w-3xl mx-auto text-center" data-id="element-179">
          <FadeIn data-id="element-180">
            <ShieldCheck className="w-14 h-14 md:w-20 md:h-20 text-[#E85D75] mx-auto mb-4 md:mb-6" data-id="element-181" />
            <h2 className="text-2xl md:text-4xl font-['Outfit'] font-bold text-slate-900 mb-4 md:mb-6" data-id="element-182">
              🛡️ GARANTÍA DE 7 DÍAS
            </h2>
            <p className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8" data-id="element-183">
              Descargá el libro. Leelo. Aplicá las herramientas. Y si sentís que
              no te aporta valor, te devolvemos el 100% de tu dinero.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-lg font-bold text-slate-800" data-id="element-184">
              <span className="bg-white px-6 py-2 rounded-full shadow-sm border border-pink-100" data-id="element-185">
                Sin preguntas.
              </span>
              <span className="bg-white px-6 py-2 rounded-full shadow-sm border border-pink-100" data-id="element-186">
                Sin riesgos.
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-24 px-4 bg-white" data-id="element-187">
        <div className="max-w-3xl mx-auto" data-id="element-188">
          <FadeIn className="text-center mb-8 md:mb-16" data-id="element-189">
            <h2 className="text-2xl md:text-4xl font-['Outfit'] font-bold text-slate-900" data-id="element-190">
              PREGUNTAS FRECUENTES
            </h2>
          </FadeIn>

          <div className="space-y-3 md:space-y-4" data-id="element-191">
            {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-192">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left bg-slate-50 hover:bg-slate-100 p-4 md:p-6 rounded-2xl transition-colors flex justify-between items-center" data-id="element-193">
                  <span className="font-bold text-slate-900 pr-8" data-id="element-194">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="text-[#E85D75] shrink-0" data-id="element-195" /> : <ChevronDown className="text-slate-400 shrink-0" data-id="element-196" />}
                </button>
                {openFaq === i && <div className="p-6 text-slate-600 bg-white border border-slate-50 rounded-b-2xl -mt-2 pt-4" data-id="element-197">
                    {faq.a}
                  </div>}
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-24 px-4 bg-slate-900 text-white text-center" data-id="element-198">
        <div className="max-w-3xl mx-auto" data-id="element-199">
          <FadeIn data-id="element-200">
            <h2 className="text-2xl md:text-5xl font-['Outfit'] font-bold mb-6 md:mb-12 leading-tight" data-id="element-201">
              ⚖️ ¿CUÁL ES EL COSTO DE NO EMPEZAR HOY?
            </h2>
            <div className="space-y-2 md:space-y-4 text-base md:text-lg text-slate-300 mb-6 md:mb-12" data-id="element-202">
              <p data-id="element-203">Seguir viviendo con ansiedad.</p>
              <p data-id="element-204">Seguir creyendo todo lo que piensa tu mente.</p>
              <p data-id="element-205">Seguir postergando tu bienestar.</p>
              <p data-id="element-206">Seguir dejando que los demás decidan por vos.</p>
              <p data-id="element-207">Seguir sin conocerte realmente.</p>
            </div>
            <div className="bg-slate-800 p-5 md:p-8 rounded-2xl mb-6 md:mb-12 border border-slate-700" data-id="element-208">
              <p className="text-lg md:text-xl font-medium text-white mb-2" data-id="element-209">
                La salud mental no cambia sola.
              </p>
              <p className="text-lg md:text-xl font-medium text-white mb-4 md:mb-6" data-id="element-210">
                Empieza cuando decidís trabajar en vos.
              </p>
              <p className="text-xl md:text-2xl font-bold text-[#E85D75]" data-id="element-211">
                Hoy puede ser ese día.
              </p>
            </div>
            <button onClick={scrollToPricing} className="w-full sm:w-auto bg-[#E85D75] hover:bg-[#D44A62] text-white text-xl font-bold py-5 px-10 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1" data-id="element-212">
              👉 QUIERO EMPEZAR MI CAMBIO HOY
            </button>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-500 py-8 text-center text-sm" data-id="element-213">
        <p data-id="element-214">© 2026 Terapia Para Llevar™ — Todos los derechos reservados.</p>
      </footer>
    </div>;
}